import { GridCellParams, GridColDef, GridColumnHeaderParams, GridValidRowModel } from '@mui/x-data-grid-pro';
import PurchaseOrderCell from './Cells/PurchaseOrderCell/PurchaseOrderCell';
import ActionsCell from './Cells/ActionsCell/ActionsCell';
import StartDateCell from './Cells/StartDateCell/StartDateCell';
import EndDateCell from './Cells/EndDateCell/EndDateCell';
import PriceCell from './Cells/PriceCell/PriceCell';
import SpecRefCell from './Cells/SpecRefCell/SpecRefCell';
import RefChronoCell from './Cells/RefChronoCell/RefChronoCell';
import DefaultCell from './Cells/DefaultCell/DefaultCell';

import { find, forEach, map } from 'lodash';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PurchaseOrders from 'types/entities/PurchaseOrders';
import { useSelector } from 'store/hooks';

type PurchaseOrderCellParams<T> = GridCellParams<PurchaseOrders, T, any>;
// type HelpMenuGuideGridCellParams<T> = GridCellParams<HelpMenuUserGuide, T, any>;

export const field = {
  PURCHASEORDER: 'poref',
  STARTDATE: 'postart',
  ENDDATE: 'poend',
  PRICE: 'poservice',
  SPECREFERENCE: 'specref_ws',
  REFERENCECHRONO: 'referencechrono_offreref',
  ACTIONS: 'actions',
  DEFAULT: 'file',
} as const;

const defaultColumnParams: GridColDef = {
  field: '',
  headerName: '',
  align: 'left',
  headerAlign: 'center',
  filterable: false,
  sortable: true,
  flex: 1,
};

export const columnsParams: GridColDef[] = [
  {
    field: field.PURCHASEORDER,
    headerName: 'Purchase Order',
    renderCell: (params: PurchaseOrderCellParams<number>) => <PurchaseOrderCell {...(params.row as any)} />,
    resizable: true,
    align: 'center',
    minWidth: 60,
  },
  {
    field: field.STARTDATE,
    headerName: 'Start Date',
    renderCell: (params: PurchaseOrderCellParams<number>) => <StartDateCell {...(params.row as any)} />,
    resizable: true,
    align: 'center',
    minWidth: 60,
  },
  {
    field: field.ENDDATE,
    headerName: 'End Date',
    renderCell: (params: PurchaseOrderCellParams<number>) => <EndDateCell {...(params.row as any)} />,
    resizable: true,
    align: 'center',
    minWidth: 60,
  },
  {
    field: field.PRICE,
    headerName: 'Price',
    renderCell: (params: PurchaseOrderCellParams<number>) => <PriceCell {...(params.row as any)} />,
    resizable: true,
    align: 'center',
    minWidth: 60,
  },
  {
    field: field.SPECREFERENCE,
    headerName: 'Spec.Reference',
    renderCell: (params: PurchaseOrderCellParams<number>) => <SpecRefCell {...(params.row as any)} />,
    resizable: true,
    align: 'center',
    minWidth: 60,
  },
  {
    field: field.REFERENCECHRONO,
    headerName: 'Reference (Chrono)',
    renderCell: (params: PurchaseOrderCellParams<number>) => <RefChronoCell {...(params.row as any)} />,
    resizable: true,
    align: 'center',
    minWidth: 60,
  },
  {
    field: field.ACTIONS,
    headerName: 'Actions',
    renderCell: (params: PurchaseOrderCellParams<null>) => <ActionsCell {...(params.row as any)} />,
    resizable: true,
    align: 'center',
    minWidth: 130,
    sortable: false,
  },
  {
    field: field.DEFAULT,
    headerName: 'Default',
    renderCell: (params: PurchaseOrderCellParams<number>) => <DefaultCell {...(params.row as any)} />,
    resizable: true,
    align: 'center',
    minWidth: 60,
    sortable: false,
  },
];

type ColumnsFields = { [name: string]: string[] };

export const PurchaseOrderFields: ColumnsFields = {
  purchaseOrder: [
    field.PURCHASEORDER,
    field.STARTDATE,
    field.ENDDATE,
    field.PRICE,
    field.SPECREFERENCE,
    field.REFERENCECHRONO,
    field.ACTIONS,
    field.DEFAULT,
  ],
};

/**
 * React hook to build columns variable for Mui Datagrid with specific components
 */
export const useColumns = (columnsFields: ColumnsFields, order_type_id: number) => {
  const [t] = useTranslation();
  const state = useSelector((state) => state.purchaseOrder);
  return useMemo(() => {
    const columns: { [name: string]: GridColDef<PurchaseOrders, any, any>[] } = {};
    forEach(columnsFields, (fields, name) => {
      columns[name] = map(fields, (field) => {
        const columnParam = find(columnsParams, ['field', field]);
        return {
          ...defaultColumnParams,
          ...columnParam,
          headerName: columnParam?.headerName ? t(columnParam.headerName) : t(field),
        };
      });
    });

    return columns;
  }, [columnsFields, t, state]);
};
